/**
 * OC Post Discussion Component
 * Uses useOcDraftPosting hook + LoadingGuard pattern
 * Follows the exact pattern from the original PostDiscussion component
 */

import '../../../../styles/oc-utilities.css';

import React, { useCallback } from 'react';

import { OcChatConfig } from '../../../../types/oc';
import { SubmitDiscussionResponse } from '../../types/apiTypes';
import AskHumanForm from './AskHumanForm';
import { useOcDraftPosting } from './hooks/useOcDraftPosting';
import {
  ErrorAlert,
  ErrorHelpText,
  LoadingContainer,
  LoadingText,
} from './OcPostDiscussion.styled';

export type OcPostDiscussionProps = {
  config: OcChatConfig;
  sessionId: string;
  onCancel?: () => void;
  onSuccess?: (response: SubmitDiscussionResponse) => void;
  onError?: (error: Error) => void;
  formId?: string;
  isSubmitting?: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
};

/**
 * Loading state component
 */
const PostDiscussionLoading: React.FC = () => {
  return (
    <LoadingContainer>
      <LoadingText>Loading draft...</LoadingText>
    </LoadingContainer>
  );
};

/**
 * OcPostDiscussion Component
 * Uses LoadingGuard pattern - load draft, show loading/error/success states
 */
const OcPostDiscussion: React.FC<OcPostDiscussionProps> = ({
  config,
  sessionId,
  onSuccess,
  onError,
  formId,
  isSubmitting,
  onSubmittingChange,
}) => {
  const { isLoading, isError, data, error } = useOcDraftPosting(config, sessionId, true);

  // Submission handler - similar to original's onSubmit
  const handleSubmit = useCallback(
    async (url: string) => {
      // In original, this does window.location.href = url
      // In our case, we call onSuccess with the response
      if (onSuccess) {
        onSuccess({ success: true, discussionUrl: url });
      } else {
        window.location.href = url;
      }
    },
    [onSuccess],
  );

  // Loading state
  if (isLoading) {
    return <PostDiscussionLoading />;
  }

  // Error state - still show form so user can post manually
  if (isError) {
    return (
      <>
        <ErrorAlert role="alert">
          Failed to load AI draft: {error?.message}
        </ErrorAlert>
        <ErrorHelpText>You can still create a post manually.</ErrorHelpText>
        <AskHumanForm
          config={config}
          draft={{ Subject: '', Message: '', TopicTagKeys: [] }}
          onSubmit={handleSubmit}
          onError={onError}
          formId={formId}
          isSubmitting={isSubmitting}
          onSubmittingChange={onSubmittingChange}
        />
      </>
    );
  }

  // Success state - render form with draft data
  if (data) {
    return (
      <AskHumanForm
        config={config}
        draft={data}
        onSubmit={handleSubmit}
        onError={onError}
        formId={formId}
        isSubmitting={isSubmitting}
        onSubmittingChange={onSubmittingChange}
      />
    );
  }

  return null;
};

export default React.memo(OcPostDiscussion);
